import { Button, Tooltip, Typography } from '@neo4j-ndl/react';
import type { FleetManagementTypes } from '@nx/state';
import { consoleApi, useActiveProject, useExternalConnectionsFlag } from '@nx/state';
import { isNonEmptyString, isNotNullish, isNullish } from '@nx/stdlib';
import { ClipboardCopier } from '@nx/ui';
import { isString } from 'lodash-es';
import { useState } from 'react';

import { hasLicenseIssues } from '../../../../../packages/state/src/slices/fleet-management/types';
import { DeploymentResources } from '../../metrics/deployment-dashboard-metrics';
import { usePermissions } from '../hooks/use-permissions';
import { ConnectDeploymentWizardModal } from '../modals/create-deplyment-modals/connect-deployment-wizard-modal';
import { DeploymentStatus } from '../shared/components/deployment-status';
import {
  UnmonitoredServersBadge,
  hasServersWithOutdatedPlugin,
  hasUnmonitoredServers,
} from '../shared/components/server-status';
import { UnknownUser } from '../shared/components/unknown-user';
import { ConnectButton, ConnectMenu } from './connect-menu';
import { DeploymentActionMenu } from './deployment-action-menu';
import { LicenseIssueBadge, OutdatedPluginBadge } from './deployment-inspect-drawer';
import { DeploymentServers } from './deployment-servers';
import { TokenStatus } from './token-management';

const PropertyDisplay = ({
  label,
  value,
  isCopyable = false,
}: {
  label: string;
  value: string | React.ReactNode;
  isCopyable?: boolean;
}) => {
  return (
    <div className="flex flex-row items-center gap-1">
      <Typography variant="body-small" className="text-neutral-text-weak">
        {label}
      </Typography>
      <Typography variant="body-small">{value}</Typography>
      {isCopyable && isString(value) && <ClipboardCopier textToCopy={value} ariaLabel="Copy to clipboard" />}
    </div>
  );
};

const CardDetails = ({
  deployment,
  projectId,
  creator,
  handleDisplayDbmsDetails,
}: {
  deployment: FleetManagementTypes.DeploymentDetails;
  projectId: string;
  creator: string | undefined;
  handleDisplayDbmsDetails: (deployment: FleetManagementTypes.DeploymentDetails) => void;
}) => {
  const [isConnectDeploymentWizardModalOpen, setIsConnectDeploymentWizardModalOpen] = useState(false);
  const isTokenUpdateAllowed = usePermissions().isAllowed('tokens', 'write');
  const externalConnectionsEnabled = useExternalConnectionsFlag();

  const isConnected = isNotNullish(deployment.dbms);

  return (
    <>
      <ConnectDeploymentWizardModal
        isOpen={isConnectDeploymentWizardModalOpen}
        closeModal={() => setIsConnectDeploymentWizardModalOpen(false)}
        deploymentId={deployment.deploymentId}
      />
      <div
        key={deployment.deploymentId}
        aria-label={`Deployment card for ${deployment.name}`}
        className="border-neutral-border-weak bg-neutral-bg-weak relative shrink-0 grow basis-[136px] rounded-2xl border"
      >
        <div className="mb-3 flex items-start justify-between gap-4 overflow-hidden px-4 pt-4">
          <div className="flex min-w-32 flex-col flex-wrap gap-x-4 gap-y-1 overflow-hidden">
            <Typography variant="h5">{deployment.name}</Typography>
            <DeploymentStatus status={deployment.status} />
            <TokenStatus token={deployment.token} handleManageToken={() => handleDisplayDbmsDetails(deployment)} />
            {hasLicenseIssues(deployment.dbms) && (
              <LicenseIssueBadge onClick={() => handleDisplayDbmsDetails(deployment)} />
            )}
            {hasUnmonitoredServers(deployment) && <UnmonitoredServersBadge viewType="list" />}
            {hasServersWithOutdatedPlugin(deployment) && <OutdatedPluginBadge deployment={deployment} />}
          </div>
          <div className="flex flex-row items-center gap-2">
            {isNullish(deployment.token) && (
              <Tooltip type={'simple'} isDisabled={isTokenUpdateAllowed}>
                <Tooltip.Trigger hasButtonWrapper>
                  <Button
                    fill="outlined"
                    size="medium"
                    color="neutral"
                    isDisabled={!isTokenUpdateAllowed}
                    onClick={() => setIsConnectDeploymentWizardModalOpen(true)}
                  >
                    Monitor deployment
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content>Project admin role is required to manage deployments</Tooltip.Content>
              </Tooltip>
            )}
            {externalConnectionsEnabled && <ConnectMenu deployment={deployment} isDisabled={false} />}
            <DeploymentActionMenu deployment={deployment} handleDisplayDbmsDetails={handleDisplayDbmsDetails} />
          </div>
        </div>
        {isConnected && (
          <div className="flex flex-row justify-between px-4 pb-3">
            <div className="flex flex-row items-center gap-2">
              <Typography variant="body-medium" className="text-neutral-text-weaker">
                Version: {deployment.dbms?.version ?? '-'}
              </Typography>
            </div>
          </div>
        )}

        <div className="border-neutral-border-weak mx-3 mb-3 flex flex-row justify-between rounded-[8px] border px-3 py-3">
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <PropertyDisplay
              label="URL:"
              value={deployment.connectionUrl ?? '-'}
              isCopyable={isNotNullish(deployment.connectionUrl)}
            />
            {isConnected && (
              <>
                <PropertyDisplay label="Edition:" value={deployment.dbms?.edition ?? '-'} />
                <PropertyDisplay label="Packaging:" value={deployment.dbms?.packaging ?? '-'} />
                <PropertyDisplay
                  label="Servers:"
                  value={
                    deployment.dbms?.servers
                      .map((server) => server.displayName)
                      .sort((a: string, b: string) => a.localeCompare(b))
                      .join(', ') ?? '-'
                  }
                />
              </>
            )}
            <PropertyDisplay
              label="Registered by:"
              value={isNonEmptyString(creator) ? creator : <UnknownUser userId={deployment.createdBy} />}
            />
          </div>
        </div>
        {isConnected && (
          <>
            <div className="bg-neutral-bg-on-bg-weak m-3 rounded-[8px] border border-transparent">
              <DeploymentResources deployment={deployment} projectId={projectId} />
            </div>
            <div className="bg-neutral-bg-on-bg-weak m-3 rounded-[8px] border border-transparent">
              <DeploymentServers deployment={deployment} />
            </div>
          </>
        )}
      </div>
    </>
  );
};

const ConnectionCardDetails = ({
  deployment,
  creator,
  projectId,
}: {
  deployment: FleetManagementTypes.DeploymentDetails;
  creator: string | undefined;
  projectId: string;
}) => {
  const isConnected = isNotNullish(deployment.dbms);
  return (
    <>
      <div
        key={deployment.deploymentId}
        aria-label={`Deployment card for ${deployment.name}`}
        className="border-neutral-border-weak bg-neutral-bg-weak relative shrink-0 grow basis-[136px] rounded-2xl border"
      >
        <div className="mb-3 flex items-start justify-between gap-4 overflow-hidden px-4 pt-4">
          <div className="flex min-w-32 flex-col flex-wrap gap-x-4 gap-y-1 overflow-hidden">
            <Typography variant="h5">{deployment.name}</Typography>
            <DeploymentStatus status={deployment.status} />
          </div>
          <div className="flex flex-row items-center gap-2">
            <ConnectButton deployment={deployment} isDisabled={isNullish(deployment.connectionUrl)} />
          </div>
        </div>
        {isConnected && (
          <div className="flex flex-row justify-between px-4 pb-3">
            <div className="flex flex-row items-center gap-2">
              <Typography variant="body-medium" className="text-neutral-text-weaker">
                Version: {deployment.dbms?.version ?? '-'}
              </Typography>
            </div>
          </div>
        )}

        <div className="border-neutral-border-weak mx-3 mb-3 flex flex-row justify-between rounded-[8px] border px-3 py-3">
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <PropertyDisplay
              label="URL:"
              value={deployment.connectionUrl ?? '-'}
              isCopyable={isNotNullish(deployment.connectionUrl)}
            />
            {isConnected && (
              <>
                <PropertyDisplay label="Edition:" value={deployment.dbms?.edition ?? '-'} />
                <PropertyDisplay label="Packaging:" value={deployment.dbms?.packaging ?? '-'} />
                <PropertyDisplay
                  label="Servers:"
                  value={deployment.dbms?.servers.map((server) => server.address).join(', ') ?? '-'}
                />
              </>
            )}
            <PropertyDisplay
              label="Registered by:"
              value={isNonEmptyString(creator) ? creator : <UnknownUser userId={deployment.createdBy} />}
            />
          </div>
        </div>
      </div>
    </>
  );
};

type DeploymentListProps = {
  deployments: FleetManagementTypes.DeploymentDetails[];
  searchFilter: string;
  handleDisplayDbmsDetails?: (deployment: FleetManagementTypes.DeploymentDetails) => void;
  connectionCardDetails?: boolean;
};

export const DeploymentList = ({
  deployments,
  searchFilter,
  handleDisplayDbmsDetails,
  connectionCardDetails = false,
}: DeploymentListProps) => {
  const activeProject = useActiveProject();
  const getProjectMembersRes = consoleApi.useListProjectMembersQuery(activeProject.id);
  const filteredDeployments = deployments.filter((deployment) => {
    const searchTerm = searchFilter.toLowerCase();
    return deployment.name.toLowerCase().includes(searchTerm);
  });

  if (connectionCardDetails) {
    filteredDeployments.sort((a, b) => {
      const aUrl = a.connectionUrl;
      const bUrl = b.connectionUrl;
      if (isNullish(aUrl) && isNullish(bUrl)) {
        return 0;
      }
      if (isNullish(aUrl)) {
        return 1;
      }
      if (isNullish(bUrl)) {
        return -1;
      }
      return 0;
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {filteredDeployments.map((deployment) => {
        const creator = getProjectMembersRes.data?.find((member) => member.userId === deployment.createdBy)?.email;
        if (connectionCardDetails) {
          return (
            <ConnectionCardDetails
              key={deployment.deploymentId}
              deployment={deployment}
              creator={creator}
              projectId={activeProject.id}
            />
          );
        }
        if (isNullish(handleDisplayDbmsDetails)) {
          return null;
        }
        return (
          <CardDetails
            key={deployment.deploymentId}
            deployment={deployment}
            projectId={activeProject.id}
            creator={creator}
            handleDisplayDbmsDetails={handleDisplayDbmsDetails}
          />
        );
      })}
    </div>
  );
};
